import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Search, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format date from dd/mm/yyyy to "JUN-2025"
const formatExpiryDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Handle dd/mm/yyyy format
    let date;
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      date = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return ''; // Invalid date
    
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 
                        'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${month}-${year}`;
  } catch (error) {
    return '';
  }
};

const PrintLabel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({
    database_type: '',
    member_type: '',
    customer_type: '',
    member_id: '',
    name: '',
    company_name: '',
    email: '',
    designation: '',
    status: '',
    state: '',
    district: '',
    expiry_date: ''
  });
  const [labelsData, setLabelsData] = useState([]);
  const [multipleIds, setMultipleIds] = useState(['']);
  const [searchMode, setSearchMode] = useState('filters'); // 'filters' or 'multiple-ids'
  const [dispatchInfo, setDispatchInfo] = useState({}); // Store previous shipment info
  const [showLastShipment, setShowLastShipment] = useState(true); // Page-level toggle
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${API}/all-unique-states`);
        setStates(response.data.states);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    fetchStates();
  }, []);

  const fetchDistricts = async (state) => {
    if (!state) { setDistricts([]); return; }
    try {
      const response = await axios.get(`${API}/all-unique-districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  // Fetch previous shipment info for all members
  const fetchDispatchInfo = async (memberIds) => {
    try {
      const response = await axios.post(`${API}/dispatches/batch-latest`, {
        ids: memberIds,
        type: 'member'
      });
      setDispatchInfo(response.data);
    } catch (error) {
      console.error('Error fetching dispatch info:', error);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();

    setLoading(true);
    try {
      console.log('Search criteria:', searchCriteria);
      const response = await axios.post(`${API}/members/search`, searchCriteria);
      console.log('Search response:', response.data.length, 'members found');
      
      if (response.data.length === 0) {
        toast.info('No members found matching the criteria');
        setLabelsData([]);
        setLoading(false);
        return;
      }

      toast.info(`Generating QR codes for ${response.data.length} members...`);

      // Sort members by numeric part of ID
      const sortedMembers = response.data.sort((a, b) => {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        return extractNumber(a.member_id) - extractNumber(b.member_id);
      });

      // Generate QR codes on frontend (much faster!)
      const labels = await Promise.all(
        sortedMembers.map(async (member) => {
          try {
            // Generate QR code as base64 data URL on frontend
            const qrDataUrl = await QRCode.toDataURL(member.member_id, {
              width: 200,
              margin: 1,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            
            return {
              ...member,
              qr_code: qrDataUrl
            };
          } catch (error) {
            console.error(`Error generating QR for ${member.member_id}:`, error);
            return {
              ...member,
              qr_code: null
            };
          }
        })
      );
      
      console.log('Total labels generated:', labels.length);
      setLabelsData(labels);
      
      // Fetch previous shipment info for all members
      const memberIds = labels.map(l => l.member_id);
      await fetchDispatchInfo(memberIds);
      
      toast.success(`${labels.length} labels generated successfully!`);
    } catch (error) {
      console.error('Error generating labels:', error);
      toast.error('Failed to generate labels: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchCriteria({
      database_type: '',
      member_type: '',
      customer_type: '',
      member_id: '',
      name: '',
      company_name: '',
      email: '',
      designation: '',
      status: '',
      state: '',
      district: '',
      expiry_date: ''
    });
    setLabelsData([]);
    setMultipleIds(['']);
    setDistricts([]);
  };

  const addIdField = () => {
    if (multipleIds.length < 25) {
      setMultipleIds([...multipleIds, '']);
    } else {
      toast.info('Maximum 25 Member IDs allowed');
    }
  };

  const removeIdField = (index) => {
    const newIds = multipleIds.filter((_, i) => i !== index);
    setMultipleIds(newIds.length > 0 ? newIds : ['']);
  };

  const updateIdField = (index, value) => {
    const newIds = [...multipleIds];
    newIds[index] = value;
    setMultipleIds(newIds);
  };

  const handleMultipleIdsSearch = async (e) => {
    if (e) e.preventDefault();
    
    // Filter out empty IDs
    const validIds = multipleIds.filter(id => id.trim() !== '');
    
    if (validIds.length === 0) {
      toast.error('Please enter at least one Member ID');
      return;
    }

    setLoading(true);
    try {
      toast.info(`Fetching labels for ${validIds.length} member ID(s)...`);
      
      // Fetch members by IDs (exact match, case-insensitive)
      const memberPromises = validIds.map(async (id) => {
        const response = await axios.post(`${API}/members/search`, { member_id: id.trim() });
        // Filter for exact match only (case-insensitive)
        const trimmedId = id.trim().toUpperCase();
        return response.data.filter(member => member.member_id.toUpperCase() === trimmedId);
      });
      
      const responses = await Promise.all(memberPromises);
      const allMembers = responses.flat();
      
      // Check which IDs were not found
      const foundIds = allMembers.map(m => m.member_id.toUpperCase());
      const notFoundIds = validIds.filter(id => !foundIds.includes(id.trim().toUpperCase()));
      
      if (notFoundIds.length > 0) {
        toast.warning(`Member IDs not found: ${notFoundIds.join(', ')}`);
      }
      
      if (allMembers.length === 0) {
        toast.error('No members found for the entered IDs');
        setLabelsData([]);
        setLoading(false);
        return;
      }

      toast.info(`Generating QR codes for ${allMembers.length} members...`);

      // Sort members by numeric part of ID
      const sortedMembers = allMembers.sort((a, b) => {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        return extractNumber(a.member_id) - extractNumber(b.member_id);
      });

      // Generate QR codes
      const labels = await Promise.all(
        sortedMembers.map(async (member) => {
          try {
            const qrDataUrl = await QRCode.toDataURL(member.member_id, {
              width: 200,
              margin: 1,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            
            return {
              ...member,
              qr_code: qrDataUrl
            };
          } catch (error) {
            console.error(`Error generating QR for ${member.member_id}:`, error);
            return {
              ...member,
              qr_code: null
            };
          }
        })
      );
      
      setLabelsData(labels);
      
      // Fetch previous shipment info for all members
      const memberIds = labels.map(l => l.member_id);
      await fetchDispatchInfo(memberIds);
      
      toast.success(`${labels.length} labels generated successfully!`);
    } catch (error) {
      console.error('Error generating labels:', error);
      toast.error('Failed to generate labels: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    document.title = 'Member Labels';
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8 no-print">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="print-label-title">
            Print Labels
          </h1>
        </div>

        {labelsData.length === 0 && (
          <div className="form-section mb-8">
            {/* Mode Selector Tabs */}
            <div className="flex space-x-2 mb-6 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setSearchMode('filters')}
                className={`px-6 py-3 font-semibold transition-all ${
                  searchMode === 'filters'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-blue-600'
                }`}
              >
                Search by Filters
              </button>
              <button
                type="button"
                onClick={() => setSearchMode('multiple-ids')}
                className={`px-6 py-3 font-semibold transition-all ${
                  searchMode === 'multiple-ids'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-blue-600'
                }`}
              >
                Multiple Member IDs
              </button>
            </div>

            {searchMode === 'filters' ? (
              <form onSubmit={handleSearch}>
                <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>Search Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <Select value={searchCriteria.database_type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, database_type: value })}>
                    <SelectTrigger data-testid="database-type-filter">
                      <SelectValue placeholder="Select magazine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Customer Type</Label>
                  <Select value={searchCriteria.customer_type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, customer_type: value })}>
                    <SelectTrigger data-testid="customer-type-filter">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Complimentary">Complimentary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={searchCriteria.status} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, status: value })}>
                    <SelectTrigger data-testid="status-filter">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                      <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Member ID</Label>
                  <Input data-testid="member-id-input" value={searchCriteria.member_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, member_id: e.target.value })} placeholder="Enter member ID" />
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input data-testid="name-input" value={searchCriteria.name} onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })} placeholder="Enter name" />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={searchCriteria.state || 'all_states'} onValueChange={(value) => {
                    const newState = value === 'all_states' ? '' : value;
                    setSearchCriteria({ ...searchCriteria, state: newState, district: '' });
                    setDistricts([]);
                    if (newState) fetchDistricts(newState);
                  }}>
                    <SelectTrigger data-testid="state-input">
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_states">All States</SelectItem>
                      {states.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>District</Label>
                  <Select value={searchCriteria.district || 'all_districts'} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, district: value === 'all_districts' ? '' : value })} disabled={!searchCriteria.state}>
                    <SelectTrigger data-testid="district-input">
                      <SelectValue placeholder={searchCriteria.state ? 'All Districts' : 'Select state first'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_districts">All Districts</SelectItem>
                      {districts.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input data-testid="expiry-date-input" type="month" value={searchCriteria.expiry_date} onChange={(e) => setSearchCriteria({ ...searchCriteria, expiry_date: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="email-input" value={searchCriteria.email} onChange={(e) => setSearchCriteria({ ...searchCriteria, email: e.target.value })} placeholder="Enter email" />
                </div>

                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input data-testid="designation-input" value={searchCriteria.designation} onChange={(e) => setSearchCriteria({ ...searchCriteria, designation: e.target.value })} placeholder="Enter designation" />
                </div>
              </div>

                <div className="flex space-x-4">
                  <Button type="submit" disabled={loading} className="btn-primary" data-testid="generate-button">
                    <Search className="mr-2" size={18} />
                    {loading ? 'Generating...' : 'Generate Labels'}
                  </Button>
                  <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
                    Reset
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleMultipleIdsSearch}>
                <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>
                  Enter Multiple Member IDs
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Enter up to 25 member IDs to generate labels for multiple members at once.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  {multipleIds.map((id, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-1">
                        <Input
                          value={id}
                          onChange={(e) => updateIdField(index, e.target.value)}
                          placeholder={`Member ID ${index + 1}`}
                          className="w-full"
                        />
                      </div>
                      {multipleIds.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeIdField(index)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <X size={18} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex space-x-4">
                  {multipleIds.length < 25 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addIdField}
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <Plus className="mr-2" size={18} />
                      Add More ID ({multipleIds.length}/25)
                    </Button>
                  )}
                  <Button type="submit" disabled={loading} className="btn-primary">
                    <Search className="mr-2" size={18} />
                    {loading ? 'Generating...' : 'Generate Labels'}
                  </Button>
                  <Button type="button" onClick={handleReset} variant="outline">
                    Reset
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}

        {labelsData.length > 0 && (
          <>
            <div className="mb-4 no-print flex items-center space-x-4 flex-wrap gap-y-2">
              <Button onClick={handlePrint} className="btn-primary" data-testid="print-labels-button">
                <Printer className="mr-2" size={18} />
                Print Labels
              </Button>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-blue-50 border border-blue-200 px-3 py-2 rounded-md">
                <input
                  type="checkbox"
                  checked={showLastShipment}
                  onChange={(e) => setShowLastShipment(e.target.checked)}
                  data-testid="toggle-last-shipment"
                  className="w-4 h-4"
                />
                <span className="text-sm font-semibold">Show Last Shipment on Label</span>
              </label>
              <Button
                onClick={() => {
                  setLabelsData([]);
                  handleReset();
                }}
                variant="outline"
                data-testid="new-search-button"
              >
                New Search
              </Button>
            </div>

            <div className="label-container" data-testid="labels-container">
              {labelsData.map((member, index) => {
                // Calculate if address is long (needs smaller font)
                const addressLength = (member.address || '').length + (member.district || '').length + (member.state || '').length;
                const isLongAddress = addressLength > 100;
                const addressFontSize = isLongAddress ? '10px' : '12px';
                const mobileFontSize = isLongAddress ? '10px' : '12px';
                
                return (
                <div key={index} className="label-item" data-testid={`label-${member.member_id}`}>
                  <div className="flex justify-between items-start" style={{ maxWidth: '100%' }}>
                    <div className="flex-1 pr-2" style={{ minWidth: 0, overflow: 'hidden' }}>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-bold" style={{ color: 'var(--primary-blue)' }}>
                          {member.member_id}
                        </span>
                        {(member.delivery || member.plan_type) && (
                          <span className="text-xs font-bold">
                            * {deliveryLabel(member.delivery || member.plan_type)} {member.expiry_date && formatExpiryDate(member.expiry_date)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-bold">To,</div>
                      <div className="font-bold" style={{ fontSize: '13px' }}>
                        {member.initials && <span>{member.initials} </span>}
                        {member.name}
                      </div>
                      {member.designation && <div className="font-bold" style={{ fontSize: addressFontSize }}>{member.designation}</div>}
                      {member.company_name && <div className="font-bold mt-1" style={{ fontSize: addressFontSize }}>{member.company_name}</div>}
                      {member.address && <div className="font-bold mt-1" style={{ fontSize: addressFontSize, wordBreak: 'break-word' }}>{member.address}</div>}
                      <div className="font-bold" style={{ fontSize: addressFontSize }}>
                        {member.district && `${member.district} `}
                        {member.state}
                        {member.pincode && ` ${member.pincode}`}
                      </div>
                      {member.contact_no && (
                        <div className="font-bold mt-1" style={{ fontSize: mobileFontSize, wordBreak: 'break-word' }}>M. {member.contact_no}</div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end">
                      <img src={member.qr_code} alt="QR Code" className="w-16 h-16" />
                      {/* Last Shipment Box — hidden by page-level toggle */}
                      {showLastShipment && dispatchInfo[member.member_id] && (
                        <div className="mt-1 border border-gray-300 p-1 text-center" style={{ minWidth: '95px', fontSize: '7px', lineHeight: '1.4' }}>
                          <div className="font-bold" style={{ fontSize: '9px' }}>Last Shipment.</div>
                          {(dispatchInfo[member.member_id].month || dispatchInfo[member.member_id].year) && (
                            <div className="font-semibold" style={{ fontSize: '8px' }}>
                              {dispatchInfo[member.member_id].month} {dispatchInfo[member.member_id].year} Issue
                            </div>
                          )}
                          {dispatchInfo[member.member_id].dispatch_date && (
                            <div style={{ fontSize: '8px' }}>{dispatchInfo[member.member_id].dispatch_date}</div>
                          )}
                          {dispatchInfo[member.member_id].courier_type && (
                            <div className="font-bold" style={{ fontSize: '9px', marginTop: '2px', lineHeight: '1.1' }}>{deliveryLabel(dispatchInfo[member.member_id].courier_type)}</div>
                          )}
                          {dispatchInfo[member.member_id].delivery_status && (
                            <div className={dispatchInfo[member.member_id].delivery_status === 'failed' ? 'text-red-600 font-semibold' : ''} style={{ fontSize: '8px' }}>
                              {dispatchInfo[member.member_id].delivery_status === 'success' ? '✓ Delivered' : '✗ Failed'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );})}
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          * {
            color: black !important;
          }
          .text-gray-600, .text-gray-700 {
            color: #4a4a4a !important;
          }
          button, .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintLabel;